import { motion } from 'framer-motion';
import { Heart, Music, Users, Target } from 'lucide-react';

const ABOUT_IMAGE = "https://images.unsplash.com/photo-1672841821756-fc04525771c2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxtdXNpYyUyMGZlc3RpdmFsJTIwY29uY2VydCUyMGNyb3dkfGVufDB8fHx8MTc3NDUzNjkwMnww&ixlib=rb-4.1.0&q=85";

const About = () => {
  const values = [
    {
      icon: Heart,
      title: 'Passion for Arts',
      description: 'We are deeply passionate about Baltimore\'s vibrant arts and music scene, dedicated to showcasing the best our city has to offer.'
    },
    {
      icon: Music,
      title: 'Supporting Musicians',
      description: 'We believe in supporting local musicians and artists by providing a platform to reach wider audiences and grow their fanbase.'
    },
    {
      icon: Users,
      title: 'Community First',
      description: 'Our community is at the heart of everything we do. We bring people together through shared experiences and memorable events.'
    },
    {
      icon: Target,
      title: 'Mission Driven',
      description: 'Our mission is to make Baltimore\'s arts and culture scene accessible to everyone, ensuring no one misses out on amazing experiences.'
    }
  ];

  return (
    <div data-testid="about-page" className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={ABOUT_IMAGE}
            alt="Baltimore Arts Scene"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-purple-500 font-semibold text-lg mb-4">About Us</p>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight mb-6">
              Your Source for Baltimore's{' '}
              <span className="text-purple-500">Arts & Culture</span>
            </h1>
            <p className="text-zinc-300 text-xl leading-relaxed">
              What's Happening Baltimore is your comprehensive guide to the music, arts, and cultural events 
              that make our city unique. We're passionate about connecting people with unforgettable experiences.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-zinc-400 leading-relaxed">
                <p>
                  What's Happening Baltimore was born out of a simple idea: no one should miss out on the 
                  incredible events happening in our city. From intimate jazz performances in Fells Point 
                  to massive festivals at the Inner Harbor, Baltimore has something for everyone.
                </p>
                <p>
                  We started as a small passion project, curating events for friends and family. As word 
                  spread, our community grew, and so did our mission. Today, we're proud to be one of the 
                  most trusted sources for event discovery in Baltimore.
                </p>
                <p>
                  Through partnerships with venues, artists, and sponsors, we've been able to support the 
                  local arts scene while making it easier for residents and visitors alike to discover 
                  what makes Baltimore special.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1656283384093-1e227e621fad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxtdXNpYyUyMGZlc3RpdmFsJTIwY29uY2VydCUyMGNyb3dkfGVufDB8fHx8MTc3NDUzNjkwMnww&ixlib=rb-4.1.0&q=85"
                  alt="Baltimore Community"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-purple-600 text-white p-6 rounded-2xl">
                <p className="font-outfit text-4xl font-bold">Since</p>
                <p className="font-outfit text-5xl font-bold">2022</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-purple-500 font-semibold mb-2">What We Believe</p>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Our Core Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center hover:border-purple-500/50 transition-colors"
              >
                <div className="w-14 h-14 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-7 h-7 text-purple-500" />
                </div>
                <h3 className="font-outfit text-xl font-semibold text-white mb-3">
                  {value.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mental Health Awareness */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-zinc-900 to-zinc-900/50 border border-zinc-800 rounded-2xl p-8 md:p-12 text-center"
          >
            <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-white mb-4">
              Every life matters. Find help and hope now.
            </h2>
            <p className="text-zinc-400 mb-6 max-w-2xl mx-auto">
              We believe in supporting mental health awareness. If you or someone you know is struggling, 
              please reach out. You're not alone.
            </p>
            <a
              href="https://afsp.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <img 
                src="https://whatshappeningbaltimore.com/wp-content/uploads/2024/09/AFSP-Logo-1.png"
                alt="American Foundation for Suicide Prevention"
                className="h-16 mx-auto opacity-80 hover:opacity-100 transition-opacity"
              />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
